import { isIos } from '@yembo/yemblocks-core/dist/_util';
import axios, { AxiosResponse } from 'axios';
import { getSessionEnd, getStatuses } from 'src/queries/apiStatus';
import { endSession } from 'src/util/endSession';
import { sleep } from './index';

export const withRetries = async <T extends Record<string, unknown> | undefined>(
  request: () => Promise<AxiosResponse<T, unknown>>,
  preventLogout?: boolean
) => {
  for (let i = 1; i < 4; i++) {
    try {
      return await request();
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorData: unknown = error.response?.data || {};
        const isTimeout = error.code === 'ECONNABORTED';
        const isIosNetworkError = error.message === 'Network Error' && isIos();
        const isConcurrencyIssue = getStatuses(errorData).some((status) => status.type === 'concurrency_issue');

        // The same rule as the fetch layer (queryYemboApiCall, apiStatus.getSessionEnd):
        // a 401 on a request that carried a token signs the person out, whatever
        // `preventLogout` says; an expired session always does.
        const sentAccessToken = !!error.config?.headers?.['X-Employee-Access-Token'];
        const sessionEnd = error.response
          ? getSessionEnd(error.response.status, errorData, { logoutIfNotAuthorized: !preventLogout, sentAccessToken })
          : null;

        const shouldRetry = isTimeout || isIosNetworkError || isConcurrencyIssue;

        //check to see if there is sensitive data we don't want logged on the console
        //error.config.data contains the request payload and may need to be redacted
        if (typeof error.config?.data === 'string') {
          const formattedData = error.config?.data.toLowerCase();

          if (['password', 'accesstoken', 'access-token'].some((keyword) => formattedData?.includes(keyword))) {
            error.config.data = 'REDACTED';
          }
        }

        if (shouldRetry) {
          // incremental retries - 2, 4, 6 seconds
          await sleep(i * 2000);
          continue;
        } else if (sessionEnd) {
          endSession(sessionEnd);
        }
      }

      throw error;
    }
  }
  throw new Error('Operation timed out. Check your internet connection.');
};
